'use client'

import { useQuery } from '@tanstack/react-query'

import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { orderXClient } from '@/service/client'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { ExchangeList, ServerMessage } from '@/types/generated/public_api'

export async function fetchAccountExchanges(tradeVenue: TradeVenue): Promise<Exchange[]> {
  const { data } = await orderXClient.post<ServerMessage>('/api', {
    getExchanges: { venue: tradeVenue },
  })
  const list: ExchangeList | undefined = data.data?.exchanges
  return list?.exchanges ?? []
}

export const accountExchangeKeys = {
  all: ['accountExchanges'] as const,
  byVenue: (tradeVenue: TradeVenue) => [...accountExchangeKeys.all, tradeVenue] as const,
}

export function useAccountExchanges(tradeVenue: TradeVenue | undefined) {
  return useQuery({
    queryKey:
      tradeVenue !== undefined ? accountExchangeKeys.byVenue(tradeVenue) : accountExchangeKeys.all,
    queryFn: () => fetchAccountExchanges(tradeVenue!),
    enabled: tradeVenue !== undefined && tradeVenue !== TradeVenue.TRADE_VENUE_UNSPECIFIED,
    staleTime: 60 * 60 * 1000,
  })
}

export function useAccountPrimaryExchange(tradeVenue: TradeVenue | undefined): Exchange {
  const { data: exchanges, isPending } = useAccountExchanges(tradeVenue)
  if (exchanges && exchanges.length > 0) return exchanges[0]
  // `data` is `undefined` for two very different reasons: the query hasn't
  // resolved yet, or it resolved with a genuinely empty list. Collapsing both
  // into a hardcoded EXCHANGE_DERIBIT fallback meant every consumer of this
  // hook (InstrumentSync, position/order-form instrument lookups, chart tabs,
  // ...) would fetch and cache Deribit's instrument metadata for the entire
  // loading window of ANY account/trade-venue — including non-Deribit ones —
  // silently mixing wrong-exchange contractSize/qtyPrecision/etc. into global
  // state. Report "unspecified" while pending so callers don't treat a guess
  // as fact. Once the query has settled with zero linked exchanges, fall back
  // to the exchange implied by the trade venue itself — single-exchange venues
  // (e.g. Upbit) legitimately report no linked exchanges via getExchanges, and
  // hardcoding Deribit here made every non-Deribit single-exchange account
  // (Upbit included) silently get treated as Deribit everywhere.
  if (isPending) return Exchange.EXCHANGE_UNSPECIFIED
  return tradeVenue !== undefined
    ? toExchangeEnum(tradeVenueToExchangeKey(tradeVenue))
    : Exchange.EXCHANGE_UNSPECIFIED
}
